// ╔══════════════════════════════════════════════════════════════╗
// ║  Attendance page — records view only                          ║
// ║  (Uploading the fingerprint sheet lives on the /fingerprint   ║
// ║   page — kept separate so it appears in one place only.)      ║
// ╚══════════════════════════════════════════════════════════════╝

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { fmtAR } from '../lib/format.js';
import {
  Avatar,
  Badge,
  Btn,
  Card,
  KPI,
} from '../components/ui.jsx';
import Icon from '../components/Icon.jsx';

export default function AttendancePage() {
  const today = new Date().toISOString().slice(0, 10);
  const { data, isLoading } = useQuery({
    queryKey: ['attendance', { date: today }],
    queryFn: () =>
      api.get('/attendance', { params: { date: today, pageSize: 50 } }).then((r) => r.data),
  });

  const records = data?.records ?? [];
  const lateCount = records.filter((r) => r.lateMinutes > 0).length;
  const otCount = records.filter((r) => r.otMinutes > 60).length;
  const missingCount = records.filter((r) => (r.issues ?? []).includes('missing_out')).length;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-up">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
            إدارة الحضور
          </div>
          <h1 className="text-2xl md:text-3xl font-black mt-1">سجل الحضور</h1>
          <p className="text-sm text-ink-400 mt-1">
            سجلات حضور اليوم من بصمات الموظفين.
          </p>
        </div>
        <Link to="/fingerprint">
          <Btn variant="primary" icon="upload">رفع ملف البصمة</Btn>
        </Link>
      </div>

      {/* Today KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="حضور اليوم" value={fmtAR(records.length)} icon="check" accent />
        <KPI label="حالات تأخير" value={fmtAR(lateCount)} icon="clock" />
        <KPI label="بصمات ناقصة" value={fmtAR(missingCount)} icon="alert" />
        <KPI label="ساعات إضافية" value={fmtAR(otCount)} icon="zap" />
      </div>

      {/* Records list */}
      <Card className="overflow-hidden">
        <div className="p-5 pb-3 flex items-center justify-between">
          <div>
            <h3 className="font-bold">سجلات اليوم</h3>
            <p className="text-xs text-ink-400 mt-0.5">{fmtAR(records.length)} سجل</p>
          </div>
        </div>
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 dark:bg-ink-800/40 text-xs text-ink-500">
              <tr>
                {['الموظف', 'الدخول', 'الانصراف', 'تأخير', 'إضافي', 'الحالة'].map((h, i) => (
                  <th key={i} className="text-start font-semibold px-4 py-3 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-soft">
              {isLoading && (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-ink-400">
                    جاري التحميل…
                  </td>
                </tr>
              )}
              {!isLoading && records.length === 0 && (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-ink-400">
                    لا توجد سجلات لليوم. ارفع ملف بصمات.
                  </td>
                </tr>
              )}
              {records.map((r) => (
                <tr key={r.id} className="hover:bg-ink-50 dark:hover:bg-ink-800/40">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar name={r.employeeName ?? r.employeeId} size={32} />
                      <div>
                        <div className="font-semibold text-sm">{r.employeeName ?? '—'}</div>
                        <div className="text-[10px] text-ink-400 font-mono">{r.employeeId}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 tabular font-semibold">{r.inTime ?? '—'}</td>
                  <td className="px-4 py-3 tabular font-semibold">{r.outTime ?? '—'}</td>
                  <td className="px-4 py-3">
                    {r.lateMinutes > 0 ? (
                      <Badge tone="warn">+{r.lateMinutes}د</Badge>
                    ) : (
                      <span className="text-emerald-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {r.otMinutes > 0 ? (
                      <Badge tone="brand">+{Math.round(r.otMinutes / 60 * 10) / 10}س</Badge>
                    ) : (
                      <span className="text-ink-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(r.issues ?? []).includes('late') && <Badge tone="warn">تأخير</Badge>}
                      {(r.issues ?? []).includes('ot') && <Badge tone="brand">إضافي</Badge>}
                      {(r.issues ?? []).includes('missing_out') && (
                        <Badge tone="danger" dot>بصمة ناقصة</Badge>
                      )}
                      {(r.issues ?? []).length === 0 && <Badge tone="success" dot>سليم</Badge>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-soft">
          {records.map((r) => (
            <div key={r.id} className="p-4">
              <div className="flex items-center gap-3">
                <Avatar name={r.employeeName ?? r.employeeId} size={36} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{r.employeeName ?? '—'}</div>
                  <div className="text-[10px] text-ink-400 font-mono">{r.employeeId}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                <div className="p-2 rounded-lg bg-ink-50 dark:bg-ink-800/40">
                  <div className="text-ink-400">دخول</div>
                  <div className="font-bold tabular">{r.inTime ?? '—'}</div>
                </div>
                <div className="p-2 rounded-lg bg-ink-50 dark:bg-ink-800/40">
                  <div className="text-ink-400">انصراف</div>
                  <div className="font-bold tabular">{r.outTime ?? '—'}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {(r.issues ?? []).includes('late') && <Badge tone="warn">تأخير {r.lateMinutes}د</Badge>}
                {(r.issues ?? []).includes('ot') && <Badge tone="brand">إضافي</Badge>}
                {(r.issues ?? []).includes('missing_out') && <Badge tone="danger" dot>ناقصة</Badge>}
                {(r.issues ?? []).length === 0 && <Badge tone="success" dot>سليم</Badge>}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
